import {
  EventCouponTypeEnum,
  EventPrivacyEnum,
  GetEventCouponsQuery,
} from 'types/generated/client';

export const PROCESSING_FEE = 0.04;
export const PLATFORM_FEE_CENTS = 0; // In cents

type Registration = {
  groupId: string;
  partnerEmail?: string | null | undefined;
  partnerName?: string | null | undefined;
};

type Event = {
  registrationPriceUnitAmount?: number | null;
  privacy: string;
  groups: {
    id: string;
    priceUnitAmount: number;
    title: string;
    timeSlots?: { id: string }[] | null;
  }[];
  isQuickCreate: boolean;
};

export type EventCoupon = GetEventCouponsQuery['eventCoupons'][number];

/**
 * Minimal coupon shape needed to compute a discount. Deliberately structural
 * (not tied to a generated query type) so the same calculation runs identically
 * in the browser and in the server-side register API, where the coupon comes
 * from a different generated type. The client `EventCoupon` satisfies this.
 */
export interface CouponForCalculation {
  couponType: string;
  percentOff?: number | null;
  amountOff?: number | null;
  fixedPrice?: number | null;
  couponGroups: { eventGroupId: string }[];
}

type Params = {
  registration: Registration[];
  event: Event;
  isFirstTournamentRegistration: boolean;
  coupon?: CouponForCalculation | null;
};

const REGISTRATION_FEE_LINE = 'registrationFee';

type DiscountableLine = { key: string; amount: number };

/**
 * Splits a discount across lines in proportion to their price so every transaction item
 * records what was actually paid for it. Cents lost to flooring go to the first lines
 * that still have room, so the line totals always sum to subtotal minus discount.
 */
function allocateDiscount(discount: number, lines: DiscountableLine[]): Map<string, number> {
  const allocations = new Map<string, number>();
  const applicable = lines.reduce((sum, line) => sum + line.amount, 0);
  if (discount <= 0 || applicable <= 0) return allocations;

  const target = Math.min(discount, applicable);
  let remainder = target;
  for (const line of lines) {
    const share = Math.min(Math.floor((target * line.amount) / applicable), line.amount);
    allocations.set(line.key, share);
    remainder -= share;
  }
  for (const line of lines) {
    if (remainder <= 0) break;
    const share = allocations.get(line.key) ?? 0;
    const extra = Math.min(line.amount - share, remainder);
    allocations.set(line.key, share + extra);
    remainder -= extra;
  }
  return allocations;
}

function computeCouponDiscount(
  coupon: CouponForCalculation,
  subtotalBeforeDiscount: number,
): number {
  switch (coupon.couponType) {
    case EventCouponTypeEnum.PercentOff:
      return Math.round(subtotalBeforeDiscount * ((coupon.percentOff ?? 0) / 100));
    case EventCouponTypeEnum.AmountOff:
      return Math.min(coupon.amountOff ?? 0, subtotalBeforeDiscount);
    case EventCouponTypeEnum.FixedPrice:
      return Math.max(0, subtotalBeforeDiscount - (coupon.fixedPrice ?? 0));
    default:
      return 0;
  }
}

export const calculateEventOrderTotal = ({
  registration,
  event,
  isFirstTournamentRegistration,
  coupon,
}: Params) => {
  if (!registration || !event) {
    return {
      registrationFee: 0,
      registrationFeeTotalUnitAmount: 0,
      registrationGroups: [],
      subtotal: 0,
      discountUnitAmount: 0,
      processingFee: 0,
      total: 0,
      applicationFeeTotal: 0,
      platformFeeUnitAmount: 0,
    };
  }

  // Incoming Tournament Price
  const orderSubtotalUnitAmount = event.registrationPriceUnitAmount || 0;

  // Conditionally set the platform fee based on event privacy
  const platformFeeCents = event.privacy === EventPrivacyEnum.Private ? 0 : PLATFORM_FEE_CENTS;
  const platformApplicationFeeUnitAmount = Math.ceil(platformFeeCents);

  const registrationFee =
    isFirstTournamentRegistration && !event.isQuickCreate ? orderSubtotalUnitAmount : 0;

  const registrationGroups: {
    id: string;
    priceUnitAmount: number;
    title: string;
    /** Dated sessions the price covers; more than one means a pack. */
    sessionCount: number;
  }[] = [];

  registration.forEach((group) => {
    const matchedGroup = event.groups.find((g) => g.id === group.groupId);
    if (matchedGroup) {
      registrationGroups.push({
        id: group.groupId,
        priceUnitAmount: matchedGroup.priceUnitAmount,
        title: matchedGroup.title,
        sessionCount: matchedGroup.timeSlots?.length || 1,
      });
    }
  });

  const groupTotal = registrationGroups.reduce((acc, group) => {
    return acc + group.priceUnitAmount;
  }, 0);

  const subtotal = registrationFee + groupTotal;

  // Coupon applies only to groups it's scoped to (empty couponGroups = all groups, fee included)
  const appliesToAllGroups = !coupon || coupon.couponGroups.length === 0;
  const scopedGroupIds = new Set(coupon?.couponGroups.map((cg) => cg.eventGroupId) ?? []);
  const isGroupDiscountable = (groupId: string) =>
    appliesToAllGroups || scopedGroupIds.has(groupId);

  const discountableLines: DiscountableLine[] = [
    ...(appliesToAllGroups && registrationFee > 0
      ? [{ key: REGISTRATION_FEE_LINE, amount: registrationFee }]
      : []),
    ...registrationGroups
      .filter((g) => isGroupDiscountable(g.id) && g.priceUnitAmount > 0)
      .map((g) => ({ key: g.id, amount: g.priceUnitAmount })),
  ];
  const applicableSubtotal = coupon
    ? discountableLines.reduce((sum, line) => sum + line.amount, 0)
    : 0;

  const discountUnitAmount = coupon ? computeCouponDiscount(coupon, applicableSubtotal) : 0;
  const lineDiscounts = allocateDiscount(discountUnitAmount, discountableLines);

  const registrationFeeTotalUnitAmount =
    registrationFee - (lineDiscounts.get(REGISTRATION_FEE_LINE) ?? 0);
  const registrationGroupsWithTotals = registrationGroups.map((g) => ({
    ...g,
    totalUnitAmount: g.priceUnitAmount - (lineDiscounts.get(g.id) ?? 0),
  }));

  const discountedSubtotal = Math.max(0, subtotal - discountUnitAmount);

  const processingFee = discountedSubtotal * PROCESSING_FEE;

  const total = discountedSubtotal + processingFee + platformApplicationFeeUnitAmount;

  const applicationFeeTotal = processingFee + platformApplicationFeeUnitAmount;

  return {
    registrationFee,
    registrationFeeTotalUnitAmount,
    registrationGroups: registrationGroupsWithTotals,
    subtotal,
    discountUnitAmount,
    processingFee,
    processingFeeDecimal: PROCESSING_FEE,
    processingFeePercentage: PROCESSING_FEE * 100,
    total,
    applicationFeeTotal,
    platformFeeUnitAmount: platformApplicationFeeUnitAmount,
  };
};

export type OrderTotal = ReturnType<typeof calculateEventOrderTotal>;
